import { useState } from 'react'
import { currentMonthKey, type PlanningPeriodKey } from '@crm/shared'

export interface PlanningPeriodState {
  key: PlanningPeriodKey
  /** YYYY-MM (для month/quarter) или YYYY (для year), null для custom */
  date: string | null
  from: string | null
  to: string | null
}

function currentYear(): string {
  return String(new Date().getUTCFullYear())
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

// Начало текущего календарного квартала: YYYY-01/04/07/10. Бэкенд для quarter
// берёт 3 месяца от месяца в date — передаём первый месяц квартала.
function currentQuarterStart(): string {
  const now = new Date()
  const y = now.getUTCFullYear()
  const qStartMonth = Math.floor(now.getUTCMonth() / 3) * 3 + 1 // 1/4/7/10
  return `${y}-${pad2(qStartMonth)}`
}

// По умолчанию custom-период раскрывается на текущий месяц целиком.
// Без дефолтов запрос /planning?period=custom падал ошибкой «from/to required»,
// а UI оставался пустым до ручного выбора обеих дат.
function defaultCustomRange(): { from: string; to: string } {
  const now = new Date()
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth()
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate()
  return {
    from: `${y}-${pad2(m + 1)}-01`,
    to: `${y}-${pad2(m + 1)}-${pad2(last)}`,
  }
}

export function usePlanningPeriod() {
  const [state, setState] = useState<PlanningPeriodState>(() => ({
    key: 'month',
    date: currentMonthKey(),
    from: null,
    to: null,
  }))

  const setKey = (key: PlanningPeriodKey) =>
    setState((s) => {
      if (key === 'custom') {
        const def = defaultCustomRange()
        return { key, date: null, from: s.from ?? def.from, to: s.to ?? def.to }
      }
      if (key === 'year') {
        return { key, date: currentYear(), from: null, to: null }
      }
      if (key === 'quarter') {
        return { key, date: currentQuarterStart(), from: null, to: null }
      }
      // month
      return { key, date: currentMonthKey(), from: null, to: null }
    })

  const setDate = (date: string) => setState((s) => ({ ...s, date }))

  const setCustom = (from: string, to: string) => setState({ key: 'custom', date: null, from, to })

  return { state, setKey, setDate, setCustom }
}
